import React from 'react';
import { View, Text, ViewStyle, StyleSheet, Platform } from 'react-native';
import { useStyles } from '../../hooks/useStyles';
import { addColorOpacity } from '../../shared/utils/styles';
import { FlagType, FLAG_CONFIG } from '../../shared/types/due-diligence';
import { Ionicons } from '@expo/vector-icons';

export interface FlagBadgeProps {
  flag: FlagType;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  showLabel?: boolean;
  style?: ViewStyle;
}

export function FlagBadge({ 
  flag, 
  size = 'md',
  showIcon = true,
  showLabel = true,
  style 
}: FlagBadgeProps) {
  const styles = useStyles();
  const { tokens, utils } = styles;
  const config = FLAG_CONFIG[flag];
  
  if (!config) {
    return null;
  }
  
  const badgeStyles = makeBadgeStyles(tokens, utils, flag, size);

  const sizeConfig = {
    xs: 10,
    sm: 12,
    md: 14,
    lg: 16,
  };
  
  const iconSize = sizeConfig[size || 'md'];

  return (
    <View style={[badgeStyles.container, style]}>
      {showIcon && (
        <View style={badgeStyles.iconContainer}>
          <Ionicons 
            name={config.icon as any} 
            size={iconSize} 
            color={config.color} 
          />
          {config.iconDouble && (
            <Ionicons 
              name={config.icon as any} 
              size={iconSize} 
              color={config.color}
              style={{ marginLeft: -4 }}
            />
          )}
        </View>
      )}
      {showLabel && (
        <Text style={badgeStyles.text}>
          {config.label}
        </Text>
      )}
    </View>
  );
}

function makeBadgeStyles(
  tokens: ReturnType<typeof useStyles>['tokens'],
  utils: ReturnType<typeof useStyles>['utils'],
  flag: FlagType,
  size: FlagBadgeProps['size']
) {
  const config = FLAG_CONFIG[flag];
  
  if (!config) {
    return StyleSheet.create({
      container: {},
      iconContainer: {},
      text: {},
    });
  }
  
  const sizeConfig = {
    xs: {
      paddingHorizontal: utils.spacing.xs,
      paddingVertical: 2,
      fontSize: utils.typography.sizes.xs,
      gap: 2,
    },
    sm: {
      paddingHorizontal: utils.spacing.sm,
      paddingVertical: 4,
      fontSize: utils.typography.sizes.xs,
      gap: 4,
    },
    md: {
      paddingHorizontal: utils.spacing.md,
      paddingVertical: 6,
      fontSize: utils.typography.sizes.sm,
      gap: 6,
    },
    lg: {
      paddingHorizontal: utils.spacing.md,
      paddingVertical: 8,
      fontSize: utils.typography.sizes.base,
      gap: 8,
    },
  };

  const currentSize = sizeConfig[size || 'md'];

  // Determine background color based on flag type
  const getBackgroundColor = () => {
    switch (flag) {
      case 'big_red':
      case 'red':
        return addColorOpacity(config.color, 0.15);
      case 'yellow':
        return addColorOpacity(config.color, 0.12);
      case 'green':
      case 'big_green':
        return addColorOpacity(config.color, 0.12);
      case 'clear':
        return addColorOpacity(config.color, 0.1);
      case 'pending':
      case 'not_applicable':
        return addColorOpacity(tokens.color.onSurface, 0.08);
      default:
        return addColorOpacity(tokens.color.onSurface, 0.1);
    }
  };

  return StyleSheet.create({
    container: {
      flexDirection: 'row',
      alignItems: 'center',
      alignSelf: 'flex-start',
      paddingHorizontal: currentSize.paddingHorizontal,
      paddingVertical: currentSize.paddingVertical,
      borderRadius: utils.radius.md,
      backgroundColor: getBackgroundColor(),
      borderWidth: 1,
      borderColor: addColorOpacity(config.color, 0.3),
      gap: currentSize.gap,
      ...Platform.select({
        web: {
          boxSizing: 'border-box' as any,
        },
      }),
    },
    iconContainer: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    text: {
      fontSize: currentSize.fontSize,
      fontWeight: utils.typography.weights.semibold,
      color: config.color,
      fontFamily: utils.typography.body.fontFamily,
    },
  });
}
